import { useState } from "react";
import { motion } from "framer-motion";
import { useUser } from "../context/UserContext";

// Home / identity selection screen.
// Each pseudonym creates its own PIN on first use; later visits require that PIN.
export const UserSelector = () => {
  const { users, authenticateUser, createPrivatePin } = useUser();
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isFirstUse = selected && !selected.pinConfigured;

  const cleanPin = (value) => value.replace(/\D/g, "").slice(0, 4);

  const submit = async (event) => {
    event.preventDefault();
    if (!selected || loading) return;

    setError("");

    if (pin.length !== 4) {
      setError("Choisis un code de 4 chiffres.");
      return;
    }

    if (isFirstUse && pin !== confirmPin) {
      setError("Les deux codes ne correspondent pas.");
      return;
    }

    setLoading(true);
    try {
      if (isFirstUse) {
        await createPrivatePin(selected.id, pin);
      } else {
        await authenticateUser(selected.id, pin);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && isFirstUse) {
        setError("Un code a déjà été créé pour ce pseudonyme. Reviens en arrière puis reconnecte-toi.");
      } else {
        setError(detail || "Code incorrect");
      }
      setPin("");
      setConfirmPin("");
    } finally {
      setLoading(false);
    }
  };

  const resetSelection = () => {
    setSelected(null);
    setPin("");
    setConfirmPin("");
    setError("");
  };

  return (
    <div className="selector-screen" data-testid="user-selector">
      <div className="ambient-glow" />
      <motion.div
        className="selector-inner"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <p className="selector-eyebrow">Un jeu à deux</p>
        <h1 className="selector-title">Qui es-tu&nbsp;?</h1>

        {!selected ? (
          <div className="selector-options">
            {users.map((u, i) => (
              <motion.button
                key={u.id}
                data-testid={`select-user-${u.slot}`}
                className="pseudo-btn"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.12 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setSelected(u);
                  setPin("");
                  setConfirmPin("");
                  setError("");
                }}
              >
                {u.pseudonym}
              </motion.button>
            ))}
          </div>
        ) : (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <p style={{ color: "var(--text-2)", margin: 0 }}>
              {isFirstUse ? (
                <>
                  Première connexion de <strong>{selected.pseudonym}</strong>.<br />
                  Crée ton propre code privé à 4 chiffres. Il ne sera jamais affiché à l'autre personne.
                </>
              ) : (
                <>
                  Entre ton code privé pour continuer en tant que <strong>{selected.pseudonym}</strong>.
                </>
              )}
            </p>

            <input
              autoFocus
              inputMode="numeric"
              autoComplete={isFirstUse ? "new-password" : "current-password"}
              type="password"
              value={pin}
              onChange={(e) => setPin(cleanPin(e.target.value))}
              placeholder={isFirstUse ? "Crée ton code" : "Code privé"}
              aria-label="Code privé"
              data-testid="user-pin-input"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "16px 18px",
                borderRadius: 16,
                border: "1px solid var(--line)",
                background: "var(--surface)",
                color: "var(--text)",
                fontSize: "1.1rem",
                textAlign: "center",
                letterSpacing: "0.3em",
                outline: "none",
              }}
            />

            {isFirstUse && (
              <input
                inputMode="numeric"
                autoComplete="new-password"
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(cleanPin(e.target.value))}
                placeholder="Confirme ton code"
                aria-label="Confirmation du code privé"
                data-testid="user-pin-confirm-input"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "16px 18px",
                  borderRadius: 16,
                  border: "1px solid var(--line)",
                  background: "var(--surface)",
                  color: "var(--text)",
                  fontSize: "1.1rem",
                  textAlign: "center",
                  letterSpacing: "0.3em",
                  outline: "none",
                }}
              />
            )}

            {error && <p style={{ color: "#d98b8b", margin: 0 }}>{error}</p>}

            <button
              className="btn-primary"
              type="submit"
              disabled={pin.length !== 4 || (isFirstUse && confirmPin.length !== 4) || loading}
            >
              {loading ? "Vérification…" : isFirstUse ? "Créer mon code" : "Entrer"}
            </button>

            <button
              type="button"
              onClick={resetSelection}
              style={{
                border: 0,
                background: "transparent",
                color: "var(--muted)",
                cursor: "pointer",
                padding: 10,
              }}
            >
              Changer de pseudonyme
            </button>
          </motion.form>
        )}
      </motion.div>
    </div>
  );
};

export default UserSelector;
